import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatIST } from "@/lib/dates";
import {
  SignIn, SignOut, Coffee, PlayCircle, MapPin, ShieldCheck, Warning,
  X, ArrowsOutSimple,
} from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker, Circle, Popup } from "react-leaflet";
import L from "leaflet";

const dot = (color) => new L.DivIcon({
  className: "",
  html: `<div style="background:${color};width:16px;height:16px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,.6)"></div>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});

const TYPE_META = {
  IN:           { icon: SignIn,      label: "IN",          color: "#22C55E" },
  OUT:          { icon: SignOut,     label: "OUT",         color: "#E50914" },
  BREAK_START:  { icon: Coffee,      label: "Break Start", color: "#F59E0B" },
  BREAK_END:    { icon: PlayCircle,  label: "Back to Work",color: "#3B82F6" },
};

const VERIF_COLOR = {
  VALID: "#22C55E", LOCATION_MISMATCH: "#F59E0B", SUSPICIOUS: "#FF3B30",
  NO_LOCATION: "#71717A",
};

export default function AttendanceTimeline({ record, open, onClose }) {
  const [zoomPhoto, setZoomPhoto] = useState(null);
  if (!record) return null;
  const events = (record.events || []).slice().sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const lastWithGps = [...events].reverse().find((e) => e.latitude && e.longitude);

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent side="right"
          data-testid="timeline-drawer"
          className="bg-[#0D0D10] border-l border-[#2D2D35] text-white w-full sm:max-w-xl overflow-y-auto p-0">
          <SheetHeader className="px-5 py-4 border-b border-[#2D2D35] sticky top-0 bg-[#0D0D10] z-10">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#E50914]">Dispute Timeline</p>
                <SheetTitle className="text-xl font-display text-white mt-1">{record.employee_name}</SheetTitle>
                <p className="text-[11px] text-zinc-400 font-mono mt-0.5">
                  {record.employee_id} · {record.shift} · {record.date}
                </p>
              </div>
              <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"
                      data-testid="timeline-close"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
              <Stat label="Worked" value={`${record.computed?.worked_hours || 0}h`} />
              <Stat label="Late" value={`${record.computed?.late_minutes || 0}m`} color="#F59E0B" />
              <Stat label="Status" value={record.computed?.status || "—"} color="#E50914" />
            </div>
          </SheetHeader>

          {/* Map */}
          {lastWithGps && (
            <div className="px-5 pt-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Location · last punch</div>
              <div className="h-44 rounded-md overflow-hidden border border-[#2D2D35]">
                <MapContainer center={[lastWithGps.latitude, lastWithGps.longitude]} zoom={16}
                              style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                  <TileLayer attribution='&copy; OSM' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {events.filter((e) => e.latitude && e.longitude).map((e, i) => (
                    <Marker key={i} icon={dot(TYPE_META[e.type]?.color || "#E50914")}
                            position={[e.latitude, e.longitude]}>
                      <Popup>{TYPE_META[e.type]?.label || e.type}<br/>{formatIST(e.at)}</Popup>
                    </Marker>
                  ))}
                  {lastWithGps.allowed_radius > 0 && (
                    <Circle center={[lastWithGps.latitude, lastWithGps.longitude]}
                            radius={lastWithGps.allowed_radius}
                            pathOptions={{ color: "#E50914", fillColor: "#E50914",
                                           fillOpacity: 0.08, weight: 1.2 }} />
                  )}
                </MapContainer>
              </div>
            </div>
          )}

          {/* Vertical event strip */}
          <div className="px-5 py-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Event Strip ({events.length})</div>
            {!events.length && <div className="text-xs text-zinc-500 py-6 text-center">No events on this day.</div>}
            <ol className="relative border-l border-[#2D2D35] pl-5 space-y-5">
              {events.map((e, i) => {
                const meta = TYPE_META[e.type] || { icon: PlayCircle, label: e.type, color: "#A1A1AA" };
                const Icon = meta.icon;
                const verifColor = VERIF_COLOR[e.verification_status] || "#71717A";
                return (
                  <li key={i} className="relative" data-testid={`tl-event-${i}`}>
                    <span className="absolute -left-[33px] flex items-center justify-center w-6 h-6 rounded-full bg-[#16161C] border border-[#2D2D35]"
                          style={{ borderColor: meta.color }}>
                      <Icon size={12} weight="bold" style={{ color: meta.color }} />
                    </span>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: meta.color }}>{meta.label}</span>
                      <span className="font-mono text-xs text-zinc-300">{formatIST(e.at, "full")}</span>
                    </div>
                    {e.photo && (
                      <button onClick={() => setZoomPhoto(e.photo)}
                              className="relative mt-2 block w-full overflow-hidden rounded border border-[#2D2D35] hover:border-[#E50914] transition-colors"
                              data-testid={`tl-photo-${i}`}>
                        <img src={e.photo} alt="" className="w-full h-44 object-cover" />
                        <span className="absolute top-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <ArrowsOutSimple size={10} /> zoom
                        </span>
                      </button>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-1.5 text-[10px] font-mono text-zinc-400">
                      <div className="flex items-center gap-1">
                        <MapPin size={10} className="text-[#E50914]" />
                        {e.location_name || "—"}
                      </div>
                      <div>
                        {e.latitude ? `${e.latitude.toFixed(5)},${e.longitude.toFixed(5)}` : "—"}
                      </div>
                      <div>distance {Math.round(e.distance_m || 0)}m / {e.allowed_radius || 0}m</div>
                      <div>accuracy {Math.round(e.accuracy || 0)}m</div>
                    </div>
                    {e.verification_status && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                           style={{ backgroundColor: `${verifColor}22`, color: verifColor }}>
                        {e.verification_status === "VALID" ? <ShieldCheck size={10} /> : <Warning size={10} />}
                        {e.verification_status.replaceAll("_", " ")}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </SheetContent>
      </Sheet>

      {/* Full-screen photo zoom */}
      <Dialog open={!!zoomPhoto} onOpenChange={(v) => !v && setZoomPhoto(null)}>
        <DialogContent className="bg-black border-[#2D2D35] max-w-3xl p-2">
          {zoomPhoto && <img src={zoomPhoto} alt="" className="w-full h-auto rounded" data-testid="tl-photo-zoom" />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded p-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-sm mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
